import * as React from "react";
import {RouterView, LoadingViewState} from "../RouterView";
import {kernel} from "../../../ioc/IOCHelper";
import {
    Spin,
    SlotSpinHistoryRequest,
    SlotSpinHistoryService,
    SlotGame,
} from "../../../service/slots/history/SlotSpinHistoryService";
import {Symbols} from "../../../ioc/Symbols";
import {Form, FormListener} from "../../form/Form";
import {FormGroup} from "../../form/FormGroup";
import {Validators} from "../../form/Validators";
import {FormInput} from "../../form/FormInput";
import {SpinHistoryTableComponent} from "./SpinHistoryTableComponent";
import {FormDateTimePicker} from "../../form/FormDateTimePicker";
import {SelectInput} from "../../form/SelectInput";

interface State extends LoadingViewState {
    request?: SlotSpinHistoryRequest;
    spin?: Spin[];
    totalResults?: number;
    validationMap?: any;
    searching?: boolean;
    gameId?: string;
    activePage?: number;
    slotGames: SlotGame[];
}

interface Props {
}

const LIMIT = 50;

export class SlotSpinHistoryListView extends RouterView<Props, State> implements FormListener<SlotSpinHistoryRequest> {
    service: SlotSpinHistoryService;

    constructor(props: Props) {
        super(props);
        this.state = Object.assign(this.state, {
            gameId: "",
            totalResults: 0,
            spin: [],
            activePage: 0,
            validationMap: {},
            slotGames: [],
        });

        this.service = kernel.get<SlotSpinHistoryService>(Symbols.SlotSpinHistoryService);
    }

    loadContent(): Promise<any> | void {
        return this.service.listAllSlotGames().then(games => {
            this.setState({ slotGames: games });
        });
    }

    onSubmit() {
        this.search(this.state.request, 0);
    }

    search(request, pageNumber) {
        request.offset = pageNumber * LIMIT;
        this.setState({searching: true, request: request});
        this.service.getSpinHistory(request).then((res: Spin[]) => {
            this.setState({spin: res, totalResults: res.length, searching: false, activePage: pageNumber});
        }).catch(() => {
            this.setState({searching: false, totalResults: 0});
        });
    }

    formDidUpdate(formModel: SlotSpinHistoryRequest, validationMap?: any) {
        this.setState({request: formModel, validationMap: validationMap});
    }

    handleChange(event: React.FormEvent) {
        const value = (event.target as any).value;
        this.setState({gameId: value});
    }

    getPageCount(): number {
        return Math.ceil(this.state.totalResults / LIMIT);
    }

    incrementPage() {
        const pageNumber = this.state.activePage + 1;
        let req = this.state.request;
        this.search(req, pageNumber);
    }

    decrementPage() {
        const pageNumber = this.state.activePage - 1;
        let req = this.state.request;
        this.search(req, pageNumber);
    }

    renderContent(): JSX.Element {
        const slotGameOptions = [{
            id: "ALL",
            name: "All Games"
        }].concat(this.state.slotGames
            .sort((gameA, gameB) =>
                gameA.name.localeCompare(gameB.name)
            )
        );
        return (
            <div>
                <div className="ibox float-e-margins">
                    <div className="col-lg-12">
                        <h1>Slots Spin History</h1>
                    </div>
                    <div className="wrapper wrapper-content animated">
                        <div className="ibox float-e-margins" style={{
                            opacity: this.state.searching ? 0.5 : 1,
                            pointerEvents: this.state.searching ? "none" : "auto"
                        }}>
                            <div className="ibox-title">
                                <h5>Find Spin History</h5>
                            </div>
                                <div className="ibox-content">
                                    <Form className="form-horizontal" formListener={this}
                                          modelObject={this.state.request}
                                          validationMap={this.state.validationMap}>
                                        <FormGroup className="form-group" model="startTime">
                                            <label className="col-lg-2 control-label">From Date (UTC):</label>
                                            <div className="col-lg-10">
                                                <FormDateTimePicker timeZoneId="UTC" model="startTime"
                                                                    validators={[Validators.required()]}/>
                                                <span className="help-block m-b-none">Show spins from this date</span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group" model="endTime">
                                            <label className="col-lg-2 control-label">To Date (UTC):</label>
                                            <div className="col-lg-10">
                                                <FormDateTimePicker timeZoneId="UTC" model="endTime"
                                                                    validators={[Validators.required()]}/>
                                                <span className="help-block m-b-none">Show spins up to this date</span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group" model="currency">
                                            <label className="col-lg-2 control-label">Currency:</label>
                                            <div className="col-lg-10">
                                                <SelectInput className="form-control" model="currency"
                                                             values={["1", "4"]}
                                                             displayValue={value => {
                                                                 return {1: "GC", 4: "SC"}[value];
                                                             }
                                                             }
                                                />
                                                <span className="help-block m-b-none">Currency</span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group" model="gameName">
                                            <label className="col-lg-2 control-label">Game Name</label>
                                            <div className="col-lg-10">
                                                <SelectInput className="form-control" model="gameName"
                                                             values={slotGameOptions.map(g => g.id)}
                                                             displayValue={(value) => this.lookupSlotGameOptionName(value, slotGameOptions)}
                                                             sort={false}
                                                />
                                                <span className="help-block m-b-none">Show spins for this game only</span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group" model="userId">
                                            <label className="col-lg-2 control-label">User Id:</label>
                                            <div className="col-lg-10">
                                                <FormInput className="form-control" model="playerId" type="text"
                                                           validators={[Validators.required(), Validators.integer()]}/>
                                                <span className="help-block m-b-none">The unique ID of the user you are looking for</span>
                                            </div>
                                        </FormGroup>
                                        <div className="col-lg-offset-2 col-lg-10">
                                            <input className="btn btn-primary" value="Search Spin History" type="submit"
                                                   onClick={() => {
                                                       this.onSubmit()
                                                   }}/>
                                        </div>
                                    </Form>
                                    <div className="wrapper wrapper-content animated">
                                        <div className="col-lg-offset-2 col-lg-10">
                                            <button className="btn btn-primary"
                                                    onClick={() => {
                                                        this.decrementPage()
                                                    }}
                                                    disabled={this.state.activePage == 0}
                                            >
                                                Newer
                                            </button>
                                            <button className="btn btn-primary"
                                                    onClick={() => {
                                                        this.incrementPage()
                                                    }}
                                                    disabled={this.state.totalResults - LIMIT < 0}
                                            >
                                                Older
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <SpinHistoryTableComponent spins={this.state.spin}/>
                        </div>
                    </div>
            </div>
        );
    }

    private lookupSlotGameOptionName(value, options) {
        return options.find((game) => {
            return game.id === value;
        }).name
    }
}



// WEBPACK FOOTER //
// ./src/app/ui/view/slots/SlotSpinHistoryListView.tsx